import {
  type InvestmentTransferValidationError,
  InvestmentTransferValidationErrorType,
  useInvestmentTransferValidation,
} from '@egw/api'
import { IMMEDIATELY } from '@egw/constants'
import { useTranslation } from '@egw/i18n'
import { type Dispatch, type SetStateAction, useEffect, useMemo } from 'react'
import { type UseFormClearErrors, type UseFormSetError } from 'react-hook-form'
import { BankInvestmentTransferValidationError, ETransferType, TransferField, type TransferListOption } from '../types'
import { checkIfInvestmentAccount } from '../utils'

interface BankInvestmentTransferValidationProps {
  setBankInvestmentValidationError: Dispatch<SetStateAction<BankInvestmentTransferValidationError>>
  onInlineError: UseFormSetError<{
    amount?: string | undefined
    to: TransferListOption
    from: TransferListOption
  }>
  transferType: ETransferType
  toFieldOption: TransferListOption | undefined
  fromFieldOption: TransferListOption | undefined
  bankInvestmentValidationError: BankInvestmentTransferValidationError
  clearInlineError: UseFormClearErrors<{
    amount?: string | undefined
    to: TransferListOption
    from: TransferListOption
  }>
}

export const useBankInvestmentTransferValidation = ({
  fromFieldOption,
  toFieldOption,
  transferType,
  bankInvestmentValidationError,
  setBankInvestmentValidationError,
  onInlineError,
  clearInlineError,
}: BankInvestmentTransferValidationProps) => {
  const { t } = useTranslation('features-transfer')

  const fetchValidation = Boolean(
    transferType === ETransferType.BankInvestment && fromFieldOption?.value && toFieldOption?.value,
  )

  const { alias, isTo } = useMemo(() => {
    if (!fetchValidation) {
      return {
        alias: undefined,
        isTo: undefined,
      }
    }

    return {
      alias: checkIfInvestmentAccount(fromFieldOption?.accountType) ? fromFieldOption?.alias : toFieldOption?.alias,
      isTo: !checkIfInvestmentAccount(fromFieldOption?.accountType),
    }
  }, [fetchValidation, fromFieldOption, toFieldOption])

  const { data: investmentTransferValidationData, isFetching } = useInvestmentTransferValidation(
    {
      alias: alias!,
      isTo: isTo!,
    },
    {
      enabled: fetchValidation,
      staleTime: IMMEDIATELY,
    },
  )

  useEffect(() => {
    // CLEAR INLINE ERROR & BankInvestmentValidationError WHEN FETCHING VALIDATION
    if (isFetching && bankInvestmentValidationError !== BankInvestmentTransferValidationError.NA) {
      setBankInvestmentValidationError(BankInvestmentTransferValidationError.NA)
      clearInlineError(TransferField.To)
    }
  }, [fetchValidation, clearInlineError, setBankInvestmentValidationError, bankInvestmentValidationError, isFetching])

  useEffect(() => {
    if (fetchValidation && investmentTransferValidationData?.__typename === 'InvestmentTransferValidationError') {
      // WHEN AN ERROR IS RETURNED FROM THE API THIS SIGNIFIES THAT A DEPOSIT OR WITHDRAWAL IS ALREADY IN PROGRESS
      // BOTH AN INLINE ERROR IS SET ON TO FIELD AND BANK INVESTMENT VALIDATION CARD WILL DISPLAY
      // USERS WILL RECEIVE A DEPOSIT IN PROGRESS MESSAGE WHEN ATTEMPTING TO WITHDRAW FUNDS FROM INVESTMENT ACCOUNT
      // USERS WILL RECEIVE A WITHDRAWAL IN PROGRESS MESSAGE WHEN ATTEMPTING TO DEPOSIT FUNDS INTO INVESTMENT ACCOUNT
      if (
        (investmentTransferValidationData as InvestmentTransferValidationError).errorType ===
        InvestmentTransferValidationErrorType.PENDING_TRANSFER_ALREADY_EXISTS
      ) {
        onInlineError(TransferField.To, {
          type: 'custom',
          message: t(
            isTo
              ? 'bank_investment_validation_error.withdrawal_inline_error'
              : 'bank_investment_validation_error.deposit_inline_error',
          ),
        })
        setBankInvestmentValidationError(
          isTo ? BankInvestmentTransferValidationError.DEPOSIT : BankInvestmentTransferValidationError.WITHDRAWAL,
        )
      } else {
        // WHEN AN ERROR IS RETURNED FROM THE API THAT A BAD REQUEST WAS MADE
        // THIS WILL BE HANDLED AS A MODAL POP UP
        setBankInvestmentValidationError(BankInvestmentTransferValidationError.BAD_REQUEST)
      }
    }
  }, [
    bankInvestmentValidationError,
    fetchValidation,
    isTo,
    setBankInvestmentValidationError,
    onInlineError,
    t,
    investmentTransferValidationData,
  ])
}
